import { perPersonBasisLabel } from "@/lib/quoteLabelUtils";
import { groupTerms } from "@/lib/termsGrouping";
import { scaleItemsForPax } from "@/lib/itineraryCostUtils";
import { forwardRef, useMemo, Fragment } from "react";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { Calendar, Users, Clock, MapPin, CreditCard, CheckCircle, ExternalLink } from "lucide-react";
import { getCurrencySymbol } from "@/lib/currencyUtils";
import { calculateItemTotalCost, calculateAccommodationNights, calculateUniversalFeeDays } from "@/lib/itineraryCostUtils";
import { getStorageUrl } from "@/lib/storageUtils";
import { extractRegionsFromItems, calculateTotalNights, formatRegionRange } from "@/lib/quoteUtils";
import { groupItemsByDay } from "@/lib/dayGroupingUtils";
import { OngoingProgrammesSection } from "@/components/quote/OngoingProgrammesSection";
import { splitOngoingProgrammes } from "@/lib/ongoingProgrammes";

import { splitInfrastructureFees, isInfrastructureFee, infrastructureFeeLabel } from "@/lib/infrastructureFees";
import { InfrastructureFeesSection } from "@/components/quote/InfrastructureFeesSection";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { formatEquivalents } from "@/lib/currencyConversion";
import { DaySection } from "@/components/quote/DaySection";
import type { ItineraryItem } from "@/components/quote/ActivityCard";
import type { Tables } from "@/integrations/supabase/types";
import { ProgrammeSections } from "@/components/quote/ProgrammeSections";
import { programmePeriod, groupItemsBySection } from "@/lib/itinerarySections";
import { applyQuoteSelections, EMPTY_SELECTIONS, markedUpLineTotals, repriceVisibleItems, issuedTotalAnchor, tradeDiscountExcludedCost, type QuoteClientSelections } from "@/lib/quoteChoices";
import { QuoteChoicesPanel } from "@/components/quote/QuoteChoicesPanel";

type Quotation = Tables<"quotations"> & {
  leads: (Tables<"leads"> & {
    business_brands: Tables<"business_brands"> | null;
    clients?: { id: string; access_token: string | null } | null;
  }) | null;
  payment_link_url?: string | null;
  payment_status?: string | null;
  payment_received_at?: string | null;
};

interface QuoteDocumentProps {
  quote: Quotation;
  itineraryItems: ItineraryItem[];
  showPaymentSection?: boolean;
  selections?: QuoteClientSelections;
  onSelectionsChange?: (s: QuoteClientSelections) => void;
  /** Strip provider branding (logo + company name). */
  isWhiteLabel?: boolean;
  /** Replace provider branding with the client's own logo/name. */
  customBranding?: { logoUrl: string; companyName?: string | null };
  /** Force the total-investment-only view regardless of the quote setting. */
  totalOnly?: boolean;
  /** Intercept Pay Now (e.g. to require terms acceptance first). */
  onPayNow?: () => void;
}

export const QuoteDocument = forwardRef<HTMLDivElement, QuoteDocumentProps>(
  ({ quote: rawQuote, itineraryItems: allItems, showPaymentSection = true, selections, onSelectionsChange, isWhiteLabel, customBranding, totalOnly, onPayNow }, ref) => {
    const activeSelections: QuoteClientSelections =
      selections || ((rawQuote as any).client_selections?.optional_item_ids ? (rawQuote as any).client_selections : EMPTY_SELECTIONS);
    const { visible: selectedItems } = useMemo(
      () => applyQuoteSelections(allItems as any[], activeSelections),
      [allItems, activeSelections]
    ) as { visible: ItineraryItem[]; adjustment: number };
    // Quoted headcount; clients may scale up — per-person rate stays fixed.
    const quotedPax = Number((rawQuote as any).pax_count ?? rawQuote.leads?.pax_count ?? 0) || 0;
    const selectedPax = (rawQuote as any).allow_pax_scaling ? Number(activeSelections.selected_pax) || 0 : 0;
    const effectivePax = quotedPax > 0 && selectedPax >= quotedPax ? selectedPax : quotedPax;
    const paxFactor = quotedPax > 0 ? effectivePax / quotedPax : 1;
    // Scale line items so the breakdown matches the scaled total.
    const itineraryItems = useMemo(
      () => (paxFactor !== 1 ? scaleItemsForPax(selectedItems as any[], quotedPax, effectivePax) : selectedItems) as ItineraryItem[],
      [selectedItems, paxFactor, quotedPax, effectivePax]
    );
    const excludedTradeDiscountCost = useMemo(
      () => tradeDiscountExcludedCost(itineraryItems as any[]),
      [itineraryItems]
    );
    // Re-run the full pricing pipeline over the selected (and pax-scaled) items
    // so option toggles recalculate discounts, fees and markup instead of
    // subtracting a gross retail difference from the discounted base total.
    // The saved total is authoritative; repricing only moves it when the client
    // changes options or headcount.
    const anchor = useMemo(() => issuedTotalAnchor(allItems as any[], rawQuote), [allItems, rawQuote]);
    const repriced = useMemo(() => {
      const r = repriceVisibleItems(itineraryItems as any[], rawQuote, paxFactor);
      const total = r.total * anchor;
      // Absorb any drift into the markup line so breakdown rows still add up.
      return { ...r, markupAmount: Math.max(r.markupAmount + (total - r.total), 0), total };
    }, [itineraryItems, rawQuote, paxFactor, anchor]);
    const transportDiscount = repriced.transportDiscount;
    const tradeDiscountEligibleCost = Math.max(
      repriced.subtotal - transportDiscount - excludedTradeDiscountCost,
      0
    );
    const baseTotal = Number(rawQuote.total_amount || 0) * paxFactor;
    const adjustment = repriced.total - baseTotal;
    const priceForSelection = (s: QuoteClientSelections) => {
      const vis = applyQuoteSelections(allItems as any[], s).visible as any[];
      const scaled = paxFactor !== 1 ? scaleItemsForPax(vis, quotedPax, effectivePax) : vis;
      return repriceVisibleItems(scaled as any[], rawQuote, paxFactor).total * anchor;
    };
    const quote = useMemo(() => {
      const q: any = { ...rawQuote };
      q.subtotal = repriced.subtotal;
      q.transport_discount_amount = repriced.transportDiscount;
      q.discount_amount = repriced.pctDiscountAmount;
      q.discount_fixed_amount = repriced.fixedDiscountAmount;
      q.markup_amount = repriced.markupAmount;
      q.additional_fees = repriced.additionalFees;
      q.total_amount = repriced.total;
      if (quotedPax > 0) q.pax_count = effectivePax;
      if (totalOnly) q.show_line_items = false;
      return q;
    }, [rawQuote, repriced, totalOnly, effectivePax, quotedPax]) as Quotation;
    // Marked-up mode: spread markup/fees across lines so items show client prices and no fee row appears.
    const markedUp = !totalOnly && (rawQuote as any).cost_presentation === "marked_up_items";
    const quoteDiscounts =
      Number((quote as any).transport_discount_amount || 0) +
      Number((quote as any).discount_amount || 0) +
      Number((quote as any).discount_fixed_amount || 0);
    const grossProgrammeCost = markedUp ? Number(quote.total_amount || 0) + quoteDiscounts : Number(quote.subtotal || 0);
    // Each line keeps its own markup (item override or quote default).
    const markedUpLines = useMemo(
      () => (markedUp ? markedUpLineTotals(itineraryItems, Number((rawQuote as any).markup_percentage) || 0, grossProgrammeCost) : null),
      [markedUp, itineraryItems, rawQuote, grossProgrammeCost]
    );
    const linePrice = (item: any) => markedUpLines?.get(item?.id ?? item) ?? calculateItemTotalCost(item);
    const clientLogo = customBranding?.logoUrl || (rawQuote as any).client_logo_url || null;
    const whiteLabel = !!customBranding || !!clientLogo || isWhiteLabel || (rawQuote as any).is_white_label === true;
    const lead = quote.leads;
    const currencySymbol = getCurrencySymbol(lead?.currency || "ZAR");
    const brand = lead?.business_brands;
    const brandName = brand?.name || lead?.brand || "Creative Travel";
    const isPaid = quote.payment_status === "paid";
    const hasPaymentLink = !!quote.payment_link_url;
    
    // Use snapshotted pax_count if available, fallback to live lead data for legacy quotes
    const paxCount = (quote as any).pax_count ?? lead?.pax_count;
    const perPersonLabel = perPersonBasisLabel(itineraryItems as any[]);
    
    const { data: exchangeRates } = useExchangeRates();
    const usdEquivalent = lead?.currency === "ZAR" && exchangeRates?.USD_to_ZAR
      ? quote.total_amount / exchangeRates.USD_to_ZAR
      : null;
    const eurEquivalent = lead?.currency === "ZAR" && (exchangeRates as any)?.EUR_to_ZAR
      ? quote.total_amount / (exchangeRates as any).EUR_to_ZAR
      : null;

    // Optional single supplement (snapshotted on the quote, falling back to the lead)
    const singleSupplement = Number(
      (quote as any).single_supplement_amount ?? (lead as any)?.single_supplement_amount ?? 0
    );
    const hasSingleSupplement = !isNaN(singleSupplement) && singleSupplement > 0;

    // Complimentary group leader place(s) absorbed into the per person price
    const groupLeaderCount = Number(
      (quote as any).group_leader_count ?? (lead as any)?.group_leader_count ?? 0
    );
    const leaderNote =
      groupLeaderCount > 0
        ? `Includes ${groupLeaderCount} complimentary group leader place${groupLeaderCount === 1 ? "" : "s"}`
        : null;

    const supplementEquivalents =
      hasSingleSupplement && lead?.currency === "ZAR"
        ? formatEquivalents(singleSupplement, "ZAR", exchangeRates as any, { withCodes: true })
        : null;

    // Optional extras (per person, snapshotted on the quote, excluded from totals)
    const optionalExtras: Array<{ description: string; price_per_person: number }> =
      Array.isArray((quote as any).optional_extras)
        ? (quote as any).optional_extras.filter(
            (e: any) => e?.description && Number(e?.price_per_person) > 0
          )
        : [];



    // Calculate total nights and extract regions from itinerary
    const totalNights = useMemo(() => 
      calculateTotalNights(lead?.start_date, lead?.end_date), 
      [lead?.start_date, lead?.end_date]
    );
    
    const regions = useMemo(
      () =>
        extractRegionsFromItems(itineraryItems, {
          countries: (lead as any)?.countries ?? null,
          destination: (lead as any)?.destination ?? null,
        }),
      [itineraryItems, (lead as any)?.countries, (lead as any)?.destination]
    );


    console.log("Quote Lead Data:", lead, "Attached Brand:", brand);

    return (
      <div ref={ref} className="quote-print-container bg-background p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 border-b pb-6">
          <div className="flex justify-between items-start">
            <div>
              {clientLogo ? (
                <img
                  src={getStorageUrl(clientLogo)}
                  alt={customBranding?.companyName || "Logo"}
                  className="h-16 mb-3 object-contain max-w-xs"
                />
              ) : !whiteLabel && brand?.logo_url && (
                <img 
                  src={getStorageUrl(brand.logo_url)} 
                  alt={brandName}
                  className="h-16 mb-3 object-contain max-w-xs"
                  style={{ 
                    mixBlendMode: 'multiply',
                    printColorAdjust: 'exact',
                    WebkitPrintColorAdjust: 'exact'
                  }}
                />
              )}
              {(!whiteLabel || customBranding?.companyName) && (
                <h1 className="text-4xl font-serif font-bold text-foreground mb-2">
                  {whiteLabel ? customBranding?.companyName : brandName}
                </h1>
              )}
              <p className="text-xl text-muted-foreground">Travel Quotation</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Quote Number</p>
              <p className="text-lg font-semibold">{quote.quote_number}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {format(new Date(quote.created_at), "MMMM dd, yyyy")}
              </p>
            </div>
          </div>
        </div>

        {/* Client Info */}
        <div className="mb-8 bg-muted/50 rounded-lg p-6 flex justify-between items-center gap-6">
          <div className="flex-1">
            <div className="mb-4">
              <h2 className="text-2xl font-serif font-semibold">
                {((quote as any).prepared_for || lead?.client_name)
                  ? `Prepared for ${(quote as any).prepared_for || lead?.client_name}`
                  : "Client"}
              </h2>
              {lead?.group_reference && lead.group_reference.trim() !== ((quote as any).prepared_for || "").trim() && (
                <p className="text-lg font-semibold text-foreground/80 mt-1">
                  {lead.group_reference}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>
                  {lead?.start_date && format(new Date(lead.start_date), "MMM dd")}
                  {" – "}
                  {lead?.end_date && format(new Date(lead.end_date), "MMM dd, yyyy")}
                  {totalNights > 0 && (
                    <span className="text-muted-foreground ml-1">
                      ({totalNights} nights)
                    </span>
                  )}
                </span>
              </div>
              {paxCount && (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  {onSelectionsChange && quotedPax > 0 && (rawQuote as any).allow_pax_scaling ? (
                    <span className="flex items-center gap-2">
                      <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0 print:hidden"
                        disabled={paxCount <= quotedPax}
                        onClick={() => onSelectionsChange({ ...activeSelections, selected_pax: Math.max(quotedPax, paxCount - 1) })}
                        aria-label="Fewer travellers">−</Button>
                      <span>{paxCount} Travelers</span>
                      <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0 print:hidden"
                        disabled={paxCount >= 500}
                        onClick={() => onSelectionsChange({ ...activeSelections, selected_pax: paxCount + 1 })}
                        aria-label="More travellers">+</Button>
                      {paxCount !== quotedPax && (
                        <span className="text-xs text-muted-foreground print:hidden">(quoted for {quotedPax})</span>
                      )}
                    </span>
                  ) : (
                    <span>{paxCount} Travelers</span>
                  )}
                </div>
              )}
              {quote.valid_until && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>Valid until {format(new Date(quote.valid_until), "MMM dd, yyyy")}</span>
                </div>
              )}
            </div>
            
            {/* Regions Summary */}
            {regions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    {regions.map((r, idx) => (
                      <span key={r.region}>
                        <span className="font-medium">{r.region}</span>
                        <span className="text-muted-foreground ml-1">
                          {formatRegionRange(r.startDate, r.endDate)}
                        </span>
                        {idx < regions.length - 1 && <span className="text-muted-foreground">,</span>}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {quote.total_amount != null && (
            <div className="text-right border-l pl-6 border-muted-foreground/20">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Financial Summary</p>
              <div className="flex items-center justify-end gap-2 text-xl font-bold text-foreground">
                <CreditCard className="h-5 w-5" />
                <span>{currencySymbol}{quote.total_amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {usdEquivalent !== null && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  ≈ ${usdEquivalent.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {eurEquivalent !== null && ` / €${eurEquivalent.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </p>
              )}
              {paxCount && paxCount > 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  {currencySymbol}{(quote.total_amount / paxCount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {perPersonLabel}
                  {usdEquivalent !== null && (
                    <span className="block text-xs">
                      ≈ ${(usdEquivalent / paxCount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {eurEquivalent !== null && ` / €${(eurEquivalent / paxCount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </span>
                  )}
                </p>
              )}
              {leaderNote && (
                <p className="text-xs text-muted-foreground mt-1">{leaderNote}</p>
              )}

              {hasSingleSupplement && (
                <p className="text-sm text-muted-foreground mt-2">
                  Single supplement (per person in a single room): {currencySymbol}
                  {singleSupplement.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {supplementEquivalents && (
                    <span className="block text-xs">{supplementEquivalents}</span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Itinerary - Grouped by Day */}
        <div className="mb-8">
          {(quote.itinerary_layout === "sections" || lead?.itinerary_layout === "sections") && programmePeriod(lead?.start_date, lead?.end_date) && (
            <div className="mb-8 border-y py-5">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Programme Period</p>
              <p className="text-xl font-semibold">{programmePeriod(lead?.start_date, lead?.end_date)}</p>
            </div>
          )}
          {(lead as any)?.programme_notes && (
            <div className="mb-8 p-6 bg-primary/5 rounded-lg border border-primary/20">
              {/^\s*</.test((lead as any).programme_notes) ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize((lead as any).programme_notes, {
                      USE_PROFILES: { html: true },
                    }),
                  }}
                />
              ) : (
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                  {(lead as any).programme_notes}
                </div>
              )}
            </div>
          )}
          <QuoteChoicesPanel mode="hotels" showTotals={false}
          items={allItems as any}
          selections={activeSelections}
          onChange={onSelectionsChange}
          currencySymbol={currencySymbol}
          baseTotal={(rawQuote.total_amount || 0) * paxFactor}
          adjustment={adjustment}
          pax={effectivePax}
          priceForSelection={priceForSelection}
          equivalents={(n) => lead?.currency === "ZAR" ? formatEquivalents(Math.abs(n), "ZAR", exchangeRates as any, { withCodes: true }) : null}
          />
          <h2 className="text-3xl font-serif font-bold mb-6">Your Itinerary</h2>
          <div className="space-y-8">
            {(() => {
              const visible = itineraryItems.filter((i: any) => !i.is_background_cost);
              const { infrastructure, dayItems: rest } = splitInfrastructureFees(visible);
              const { ongoing, dayItems } = splitOngoingProgrammes(rest as any[]);
              return (
                <>
                  <InfrastructureFeesSection items={infrastructure as any} />
                  {(quote.itinerary_layout === "sections" || lead?.itinerary_layout === "sections") ? (
                    <ProgrammeSections
                      items={[...ongoing, ...dayItems] as any}
                      sections={quote.itinerary_sections || lead?.itinerary_sections}
                    />
                  ) : <>
                  <OngoingProgrammesSection items={ongoing as any} />
                  {groupItemsByDay(dayItems as any[], lead?.start_date).map(({ dayNumber, date, items }) => (
                    <DaySection
                      key={dayNumber}
                      dayNumber={dayNumber}
                      date={date}
                      items={items}
                    />
                  ))}
                  </>}

                </>
              );
            })()}
          </div>
        </div>

        {/* Financial Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-serif font-bold mb-4">Investment</h2>
          
          {/* Per-Item Breakdown - Only show if show_line_items is true */}
          {(quote as any).show_line_items !== false && itineraryItems.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Programme Breakdown</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left">Item</TableHead>
                    <TableHead className="text-center w-20">Qty</TableHead>
                    <TableHead className="text-right">
                      Price
                      {lead?.currency === "ZAR" && exchangeRates?.USD_to_ZAR && (
                        <div className="text-xs font-normal text-muted-foreground">(USD / EUR equiv.)</div>
                      )}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const sectionsMode = markedUp && ((quote as any).itinerary_layout === "sections" || lead?.itinerary_layout === "sections");
                    const { infrastructure, dayItems } = splitInfrastructureFees(itineraryItems);
                    const groups: { label: string | null; items: ItineraryItem[] }[] = sectionsMode
                      ? groupItemsBySection(itineraryItems, (quote as any).itinerary_sections || lead?.itinerary_sections).map((g) => ({ label: g.name, items: g.items as ItineraryItem[] }))
                      : infrastructure.length > 0
                        ? [{ label: "Infrastructure Fees", items: infrastructure }, ...(dayItems.length ? [{ label: "Programme Itinerary", items: dayItems }] : [])]
                        : [{ label: null, items: dayItems }];
                    const fmtLine = (v: number) => (
                      <>
                        <div>{currencySymbol}{v.toFixed(2)}</div>
                        {lead?.currency === "ZAR" && exchangeRates?.USD_to_ZAR && (
                          <div className="text-xs text-muted-foreground">
                            (${(v / exchangeRates.USD_to_ZAR).toFixed(2)}
                            {(exchangeRates as any)?.EUR_to_ZAR && ` / €${(v / (exchangeRates as any).EUR_to_ZAR).toFixed(2)}`})
                          </div>
                        )}
                      </>
                    );
                    return groups.map((group, gi) => (
                      <Fragment key={group.label || gi}>
                        {group.label && (
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={3} className="font-semibold uppercase text-xs tracking-wider text-muted-foreground">
                              {group.label}
                            </TableCell>
                          </TableRow>
                        )}
                        {group.items.map((item, index) => {
                    const isCustomItem = !item.products && item.custom_title;
                    const baseTitle = isCustomItem 
                      ? item.custom_title 
                      : (item.products?.product_name || item.title || item.custom_title || item.location || item.item_type.replace("_", " "));
                    
                    // Add occupancy suffix for accommodation items
                    const occupancySuffix = item.item_type === 'accommodation' && item.occupancy_type && (item as any).accommodation_type !== 'villas'
                      ? ` (${item.occupancy_type.charAt(0).toUpperCase() + item.occupancy_type.slice(1)})`
                      : '';
                    let displayTitle = baseTitle + occupancySuffix;
                    if (isInfrastructureFee(item)) {
                      displayTitle = infrastructureFeeLabel(item, baseTitle);
                    }
                    
                    // Use shared utility for correct cost calculation (handles accommodation nights)
                    const lineTotal = linePrice(item);
                    
                    // Calculate display quantity - accommodation shows nights, multi-day
                    // universal fees (Guide / S&T / Chef) show day count.
                    const nights = item.item_type === 'accommodation'
                      ? calculateAccommodationNights(item.check_in, item.check_out)
                      : 0;
                    const rooms = item.quantity || 1;
                    const ufKind = (item as any).universal_fee_kind as string | undefined;
                    const isMultiDayUF = item.item_type === 'universal_fee'
                      && (ufKind === 'guiding' || ufKind === 'assistant_guide' || ufKind === 'st' || ufKind === 'chef')
                      && !!(item as any).service_start && !!(item as any).service_end;
                    const ufDays = isMultiDayUF
                      ? calculateUniversalFeeDays((item as any).service_start, (item as any).service_end)
                      : 0;
                    const sessions = (item as any).session_count && (item as any).session_count > 1
                      ? Number((item as any).session_count)
                      : 0;
                    let displayQty: string;
                    if (item.item_type === 'accommodation' && nights > 0) {
                      displayQty = `${nights} night${nights > 1 ? 's' : ''}${rooms > 1 ? ` × ${rooms} rooms` : ''}`;
                    } else if (isMultiDayUF && ufDays > 0) {
                      const qty = item.quantity || 1;
                      const showQty = ufKind !== 'chef' && qty > 1;
                      displayQty = `${ufDays} day${ufDays > 1 ? 's' : ''}${showQty ? ` × ${qty}` : ''}`;
                    } else if (sessions > 0) {
                      const qty = item.quantity || 1;
                      displayQty = `${sessions} sessions${qty > 1 ? ` × ${qty}` : ''}`;
                    } else {
                      displayQty = (item.quantity || 1).toString();
                    }

                    
                    return (
                      <Fragment key={item.id || index}>
                        <TableRow>
                        <TableCell className="font-medium">
                          <div className="flex flex-wrap items-center gap-2">
                            <span>{displayTitle}</span>
                            {(item as any).exclude_from_trade_discount === true && (
                              <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                                Excl. from trade discount
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{displayQty}</TableCell>
                        <TableCell className="text-right">{fmtLine(lineTotal)}</TableCell>
                        </TableRow>
                      </Fragment>
                    );
                    })}
                        {sectionsMode && group.label && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-right text-sm font-medium text-muted-foreground">{group.label} subtotal</TableCell>
                            <TableCell className="text-right text-sm font-medium">
                              {fmtLine(group.items.reduce((a, i) => a + linePrice(i), 0))}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ));
                  })()}
                  <TableRow className="border-t-2 bg-muted/50">
                    <TableCell className="font-semibold" colSpan={2}>Subtotal</TableCell>
                    <TableCell className="text-right font-semibold">
                      <div>{currencySymbol}{grossProgrammeCost.toFixed(2)}</div>
                      {lead?.currency === "ZAR" && exchangeRates?.USD_to_ZAR && (
                        <div className="text-xs text-muted-foreground font-normal">
                          (${(grossProgrammeCost / exchangeRates.USD_to_ZAR).toFixed(2)}
                          {(exchangeRates as any)?.EUR_to_ZAR && ` / €${(grossProgrammeCost / (exchangeRates as any).EUR_to_ZAR).toFixed(2)}`})
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          {/* Summary - Show simplified version if line items are hidden */}
          {(quote as any).show_line_items === false ? (
            <div className="text-center py-8 px-6 bg-muted/30 rounded-lg">
              <p className="text-lg text-muted-foreground mb-3">Your Complete Travel Experience</p>
              <p className="text-4xl font-bold text-foreground">
                {currencySymbol}{quote.total_amount.toFixed(2)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">Total Investment</p>
              {usdEquivalent !== null && (
                <p className="text-lg text-muted-foreground mt-2">
                  (Approx. ${usdEquivalent.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                  {eurEquivalent !== null && ` / €${eurEquivalent.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`})
                </p>
              )}
              {paxCount && paxCount > 0 && (
                <p className="text-lg font-semibold text-foreground/80 mt-4">
                  {currencySymbol}{(quote.total_amount / paxCount).toFixed(2)} {perPersonLabel}
                  {usdEquivalent !== null && (
                    <span className="block text-base text-muted-foreground mt-1">
                      (${(usdEquivalent / paxCount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                      {eurEquivalent !== null && ` / €${(eurEquivalent / paxCount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`} {perPersonLabel})
                    </span>
                  )}
                </p>
              )}
              {leaderNote && (
                <p className="text-sm text-muted-foreground mt-2">{leaderNote}</p>
              )}

              {hasSingleSupplement && (
                <p className="text-sm text-muted-foreground mt-4">
                  Single supplement (per person in a single room): {currencySymbol}
                  {singleSupplement.toFixed(2)}
                  {supplementEquivalents && (
                    <span className="block text-xs mt-0.5">{supplementEquivalents}</span>
                  )}
                </p>
              )}
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-3">Summary</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left">Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Programme Cost{((quote as any).transport_discount_amount > 0 || (quote as any).discount_percentage > 0 || (quote as any).discount_fixed_amount > 0) ? " (Retail)" : ""}</TableCell>
                    <TableCell className="text-right font-medium">
                      {currencySymbol}
                      {grossProgrammeCost.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  {(quote as any).transport_discount_amount > 0 && (
                    <TableRow>
                      <TableCell className="text-destructive">Own Transport Discount</TableCell>
                      <TableCell className="text-right font-medium text-destructive">
                        -{currencySymbol}
                        {Number((quote as any).transport_discount_amount).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}
                  {(quote as any).discount_percentage > 0 && (quote as any).discount_amount > 0 && (
                    <TableRow>
                      <TableCell className="text-destructive">
                        Trade Discount{markedUp ? "" : <> ({(quote as any).discount_percentage}%
                        {excludedTradeDiscountCost > 0
                          ? ` on eligible items of ${currencySymbol}${tradeDiscountEligibleCost.toFixed(2)}`
                          : ""}
                        )</>}
                      </TableCell>
                      <TableCell className="text-right font-medium text-destructive">
                        -{currencySymbol}
                        {(quote as any).discount_amount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}
                  {(quote as any).discount_fixed_amount > 0 && (
                    <TableRow>
                      <TableCell className="text-destructive">Trade Discount (Fixed)</TableCell>
                      <TableCell className="text-right font-medium text-destructive">
                        -{currencySymbol}
                        {Number((quote as any).discount_fixed_amount).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}
                  {((quote as any).transport_discount_amount > 0 || (quote as any).discount_percentage > 0 || (quote as any).discount_fixed_amount > 0) && (
                    <TableRow>
                      <TableCell>Net Programme Cost</TableCell>
                      <TableCell className="text-right font-medium">
                        {currencySymbol}
                        {(grossProgrammeCost - quoteDiscounts).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}
                  {!markedUp && Array.isArray((quote as any).additional_fees) && (quote as any).additional_fees.map((fee: any, idx: number) => (
                    fee.amount > 0 && (
                      <TableRow key={idx}>
                        <TableCell>{fee.label} ({fee.percentage}%)</TableCell>
                        <TableCell className="text-right font-medium">
                          {currencySymbol}
                          {Number(fee.amount).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )
                  ))}
                  {!markedUp && quote.markup_amount > 0 && (
                    <TableRow>
                      <TableCell>Service Fee ({quote.markup_percentage}%)</TableCell>
                      <TableCell className="text-right font-medium">
                        {currencySymbol}
                        {quote.markup_amount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow className="border-t-2">
                    <TableCell className="font-bold text-lg">Total Investment</TableCell>
                    <TableCell className="text-right font-bold text-2xl">
                      {currencySymbol}
                      {quote.total_amount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  {usdEquivalent !== null && (
                    <TableRow>
                      <TableCell className="text-muted-foreground">USD / EUR Equivalent</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        ≈ ${usdEquivalent.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {eurEquivalent !== null && ` / €${eurEquivalent.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </TableCell>
                    </TableRow>
                  )}
                  {paxCount && paxCount > 0 && (
                    <TableRow>
                      <TableCell className="text-muted-foreground">Price {perPersonLabel.replace(/^per person/, "per Person")}</TableCell>
                      <TableCell className="text-right font-semibold text-lg">
                        {currencySymbol}{(quote.total_amount / paxCount).toFixed(2)}
                        {usdEquivalent !== null && (
                          <span className="block text-sm text-muted-foreground font-normal">
                            ≈ ${(usdEquivalent / paxCount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                            {eurEquivalent !== null && ` / €${(eurEquivalent / paxCount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                   )}
                  {hasSingleSupplement && (
                    <TableRow>
                      <TableCell className="text-muted-foreground">
                        Single supplement (per person in a single room)
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {currencySymbol}{singleSupplement.toFixed(2)}
                        {supplementEquivalents && (
                          <span className="block text-sm text-muted-foreground font-normal">
                            {supplementEquivalents}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </div>

        <QuoteChoicesPanel mode="activities" showTotals={false}
          items={allItems as any}
          selections={activeSelections}
          onChange={onSelectionsChange}
          currencySymbol={currencySymbol}
          baseTotal={(rawQuote.total_amount || 0) * paxFactor}
          adjustment={adjustment}
          pax={effectivePax}
          priceForSelection={priceForSelection}
          equivalents={(n) => lead?.currency === "ZAR" ? formatEquivalents(Math.abs(n), "ZAR", exchangeRates as any, { withCodes: true }) : null}
        />
        <QuoteChoicesPanel mode="optional"
          items={allItems as any}
          selections={activeSelections}
          onChange={onSelectionsChange}
          currencySymbol={currencySymbol}
          baseTotal={(rawQuote.total_amount || 0) * paxFactor}
          adjustment={adjustment}
          pax={effectivePax}
          priceForSelection={priceForSelection}
          equivalents={(n) => lead?.currency === "ZAR" ? formatEquivalents(Math.abs(n), "ZAR", exchangeRates as any, { withCodes: true }) : null}
        />

        {/* Optional Extras */}
        {optionalExtras.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-1">Optional Extras</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Per person, on request. Not included in the total investment above.
            </p>
            <Table>
              <TableBody>
                {optionalExtras.map((extra, idx) => {
                  const price = Number(extra.price_per_person);
                  const equivalents =
                    lead?.currency === "ZAR"
                      ? formatEquivalents(price, "ZAR", exchangeRates as any, { withCodes: true })
                      : null;
                  return (
                    <TableRow key={idx}>
                      <TableCell>{extra.description}</TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap">
                        {currencySymbol}
                        {price.toFixed(2)}
                        {equivalents && (
                          <span className="block text-sm text-muted-foreground font-normal">
                            {equivalents}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}



        {/* Programme-level Inclusions / Exclusions — infrastructural items that
            apply across the whole journey, distinct from the per-activity points
            shown under each activity. Hidden when the quote opts out. */}
        {!(quote as any).hide_programme_inclusions_exclusions && (quote as any).inclusions && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-1">Overall Programme Inclusions</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Applies across the entire programme — in addition to the inclusions listed under each activity above.
            </p>
            <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap">
              {(quote as any).inclusions}
            </div>
          </div>
        )}

        {!(quote as any).hide_programme_inclusions_exclusions && (quote as any).exclusions && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-1">Overall Programme Exclusions</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Applies across the entire programme — in addition to any exclusions noted under each activity above.
            </p>
            <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap">
              {(quote as any).exclusions}
            </div>
          </div>
        )}

        {/* Terms & Conditions */}
        {quote.terms_and_conditions &&
          groupTerms(quote.terms_and_conditions).map((g) => (
            <div key={g.key} className="mb-8">
              <h3 className="text-lg font-semibold mb-3">{g.title}</h3>
              <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap">
                {g.lines.join("\n")}
              </div>
            </div>
          ))}

        {/* Payment Section */}
        {showPaymentSection && (hasPaymentLink || isPaid) && (
          <div className="mb-8 p-6 rounded-lg border-2 border-secondary/20 bg-secondary/5 no-print">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-secondary/10">
                {isPaid ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <CreditCard className="h-6 w-6 text-secondary" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {isPaid ? "Payment Received" : "Secure Payment"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isPaid 
                    ? `Paid on ${quote.payment_received_at ? format(new Date(quote.payment_received_at), "MMMM dd, yyyy") : "N/A"}`
                    : "Pay securely online via Yoco"
                  }
                </p>
              </div>
            </div>
            
            {!isPaid && quote.payment_link_url && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-2xl font-bold">
                    {currencySymbol}{quote.total_amount.toFixed(2)}
                  </p>
                  {quote.leads.currency === "ZAR" && exchangeRates?.USD_to_ZAR && exchangeRates?.EUR_to_ZAR && (
                    <p className="text-sm text-muted-foreground mt-0.5">
                      ≈ ${(quote.total_amount / exchangeRates.USD_to_ZAR).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                      {" / "}
                      €{(quote.total_amount / exchangeRates.EUR_to_ZAR).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {quote.leads.currency !== "ZAR" && "Payment will be processed in ZAR"}
                  </p>
                </div>
                {onPayNow ? (
                  <Button size="lg" variant="secondary" className="gap-2 min-w-[200px]" onClick={onPayNow}>
                    <CreditCard className="h-5 w-5" />
                    Pay Now
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button size="lg" variant="secondary" className="gap-2 min-w-[200px]" asChild>
                    <a href={quote.payment_link_url} target="_blank" rel="noopener noreferrer">
                      <CreditCard className="h-5 w-5" />
                      Pay Now
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </div>
            )}

            {isPaid && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Thank you for your payment!</span>
              </div>
            )}
          </div>
        )}

        {/* Client Portal Link */}
        {lead?.clients?.access_token && (
          <div className="mb-8 p-4 rounded-lg bg-muted/30 border text-center no-print">
            <p className="text-sm text-muted-foreground mb-2">
              Track your booking status and manage your travel details:
            </p>
            <a 
              href={`${window.location.origin}/portal/client/${lead.clients.access_token}`}
              className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4" />
              View Your Client Dashboard
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="border-t pt-6 text-sm text-muted-foreground">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whiteLabel ? (
              <div>{customBranding?.companyName && <p className="font-semibold text-foreground mb-1">{customBranding.companyName}</p>}</div>
            ) : (
            <div>
              <p className="font-semibold text-foreground mb-1">{brandName}</p>
              {brand?.address && <p>{brand.address}</p>}
              {(brand?.phone || brand?.email) && (
                <p className="mt-1">
                  {brand.phone && <span>{brand.phone}</span>}
                  {brand.phone && brand.email && <span> | </span>}
                  {brand.email && <span>{brand.email}</span>}
                </p>
              )}
              {brand?.website && (
                <p className="mt-1">
                  <a href={brand.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    {brand.website}
                  </a>
                </p>
              )}
              {!brand && <p>Contact us for any queries</p>}
            </div>
            )}
            <div className="text-right">
              {quote.valid_until && (
                <p className="font-medium text-foreground">
                  This quote is valid until {format(new Date(quote.valid_until), "MMMM dd, yyyy")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

QuoteDocument.displayName = "QuoteDocument";
